import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    Typography,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    Alert,
    Divider,
    TextField,
    InputAdornment,
    Tooltip,
} from "@mui/material";
import {
    Folder as FolderIcon,
    Search as SearchIcon,
    CheckCircle as CompleteIcon,
    ReportProblem as UnreconciledIcon,
    ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../hooks/usePermissions";
import ProjectService from "../services/Project.service";
import { getGradeChipSx, getGradeLabel } from "../utils/gradeColors";

function ProjectCard({ project, onClick, showGrade }) {
    const profitScore =
        project.profit_score == null || project.profit_score === ""
            ? null
            : Number(project.profit_score);
    const hasProfitScore = profitScore != null && !Number.isNaN(profitScore);
    const gradeLabel = getGradeLabel(project.profit_grade);
    const gradeTooltip = hasProfitScore
        ? `Grade: ${project.profit_grade} (${gradeLabel}) • Score: ${profitScore}${project.profit_margin_pct != null ? ` • Margin: ${parseFloat(project.profit_margin_pct) || 0}%` : ""}`
        : "Grade unavailable";

    return (
        <Card variant="outlined" sx={{ borderColor: "primary.main" }}>
            <CardActionArea onClick={onClick}>
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                <Typography
                                    variant="subtitle2"
                                    fontWeight={700}
                                    sx={{ fontFamily: "Montserrat, sans-serif" }}
                                >
                                    {project.number}
                                </Typography>
                                <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
                                <Typography variant="subtitle2" noWrap>
                                    {project.name}
                                </Typography>
                            </Box>
                            {project.customer_name && (
                                <Typography variant="caption" color="text.secondary">
                                    {project.customer_name}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                            {showGrade && project.profit_grade && (
                                <Tooltip title={gradeTooltip} placement="top">
                                    <Chip
                                        label={project.profit_grade}
                                        size="small"
                                        variant="filled"
                                        sx={{
                                            fontWeight: 700,
                                            width: 26,
                                            height: 26,
                                            borderRadius: "50%",
                                            justifyContent: "center",
                                            ...getGradeChipSx(project.profit_grade),
                                            "& .MuiChip-label": { px: 0 },
                                        }}
                                    />
                                </Tooltip>
                            )}
                            {project.is_complete && (
                                <Chip
                                    icon={<CompleteIcon />}
                                    label="Complete"
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                />
                            )}
                            {project.has_unreconciled_bom_items && (
                                <Tooltip title="Some shipped items don't match this project's BOM">
                                    <Chip
                                        icon={<UnreconciledIcon />}
                                        label="BOM Mismatch"
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                    />
                                </Tooltip>
                            )}
                            <ChevronRightIcon fontSize="small" color="action" />
                        </Box>
                    </Box>
                </CardContent>
            </CardActionArea>
        </Card>
    );
}

// ── Subcontractor view: load all assigned projects upfront ─────────────────────
function SubcontractorProjects({ navigate }) {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");

    useEffect(() => {
        ProjectService.list({ is_archived: "false", page_size: 200 })
            .then((res) => setProjects(res.data?.results || res.data || []))
            .catch(() => setError("Failed to load projects."))
            .finally(() => setLoading(false));
    }, []);

    const filtered = projects.filter((p) => {
        const q = search.toLowerCase();
        return (
            !q ||
            p.name?.toLowerCase().includes(q) ||
            p.number?.toLowerCase().includes(q) ||
            p.customer_name?.toLowerCase().includes(q)
        );
    });

    return (
        <>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "Montserrat, sans-serif" }}>
                    My Projects
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Projects you are assigned to
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {!loading && projects.length > 1 && (
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search projects…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
            )}

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
                    <CircularProgress />
                </Box>
            ) : filtered.length === 0 ? (
                <Box sx={{ textAlign: "center", mt: 8, color: "text.secondary" }}>
                    <FolderIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
                    <Typography variant="body2">
                        {projects.length === 0
                            ? "No projects have been assigned to you yet."
                            : "No projects match your search."}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {filtered.map((p) => (
                        <ProjectCard key={p.id} project={p} onClick={() => navigate(`/projects/${p.id}`)} />
                    ))}
                </Box>
            )}
        </>
    );
}

// ── Employee view: search-first, server-side ───────────────────────────────────
function EmployeeProjects({ navigate }) {
    const [search, setSearch] = useState("");
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [hasSearched, setHasSearched] = useState(false);
    const debounceRef = useRef(null);

    const runSearch = useCallback((term) => {
        setLoading(true);
        setError("");
        ProjectService.list({ is_archived: "false", search: term, page_size: 50 })
            .then((res) => setProjects(res.data?.results || res.data || []))
            .catch(() => setError("Failed to load projects."))
            .finally(() => setLoading(false));
    }, []);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearch(val);
        clearTimeout(debounceRef.current);
        if (!val.trim()) {
            setProjects([]);
            setHasSearched(false);
            return;
        }
        setHasSearched(true);
        debounceRef.current = setTimeout(() => runSearch(val.trim()), 350);
    };

    return (
        <>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "Montserrat, sans-serif" }}>
                    Projects
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Search by name, number, or customer
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TextField
                size="small"
                fullWidth
                autoFocus
                placeholder="Search projects…"
                value={search}
                onChange={handleSearchChange}
                sx={{ mb: 2 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                        </InputAdornment>
                    ),
                    endAdornment: loading ? (
                        <InputAdornment position="end">
                            <CircularProgress size={16} />
                        </InputAdornment>
                    ) : null,
                }}
            />

            {!hasSearched ? (
                <Box sx={{ textAlign: "center", mt: 8, color: "text.secondary" }}>
                    <SearchIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                    <Typography variant="body2">Start typing to search projects</Typography>
                </Box>
            ) : projects.length === 0 && !loading ? (
                <Box sx={{ textAlign: "center", mt: 8, color: "text.secondary" }}>
                    <FolderIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
                    <Typography variant="body2">No projects match your search.</Typography>
                </Box>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {projects.map((p) => (
                        <ProjectCard
                            key={p.id}
                            project={p}
                            showGrade
                            onClick={() => navigate(`/projects/${p.id}`)}
                        />
                    ))}
                </Box>
            )}
        </>
    );
}

// ── Page entry point ───────────────────────────────────────────────────────────
function Projects() {
    const navigate = useNavigate();
    const { isSubcontractor } = usePermissions();

    return (
        <Box>
            {isSubcontractor
                ? <SubcontractorProjects navigate={navigate} />
                : <EmployeeProjects navigate={navigate} />
            }
        </Box>
    );
}

export default Projects;
